import React, { useState, useEffect } from "react";
import QuestionInfoHeader from "./QuestionInfoHeader";
import { Box, Typography } from "@mui/material";
import ButtonSection from "./ButtonSection";
import { useLanguages } from "../../../context/LanguagesContext";

function QuestionSection({
  questionData = [],
  currentQuestion = 0,
  handleOptionChange,
  handleSaveNextDefault,
  handleMarkReviewDefault,
  handleClearResponse,
  selectedOptionId,
  handleBack,
  currentQuestionBySubject,
  handleOpenSubmitModal,
  setCurrentQuestionBySubject
}) {
  const [question, setQuestion] = useState(null);
  const [optionId, setOptionId] = useState(selectedOptionId); // ✅ store selected option ID
  const { languages, handleChange, selectedLanguage } = useLanguages();
  const handleOptionSelect = (id) => {
    setOptionId(id);
    handleOptionChange({ target: { value: id } });
  };

  useEffect(() => {
    if (Array.isArray(questionData) && questionData.length > 0) {
      const currentQ = questionData[currentQuestion];
      setQuestion(currentQ);
      setOptionId(selectedOptionId || null);
    }
  }, [currentQuestion, questionData]);
  useEffect(() => {
    if (selectedOptionId) {
      handleOptionSelect(selectedOptionId);
    }
  }, [selectedOptionId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: { xs: "100%", md: "80%" },
        // Fixed height so content scrolls and buttons stay visible
        height: { xs: "calc(100vh - 160px)", md: "calc(100vh - 160px)" },
        borderRight: { xs: "none", md: "1px solid #e0e0e0" },
        background: "#fff",
        overflowX: "hidden",
        overflowY: "hidden",  // container does NOT scroll; only inner content box scrolls
      }}
    >
      {question && (
        // flex:1 stretches this to fill remaining height, pushing ButtonSection to bottom
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <QuestionInfoHeader
            currentQuestion={currentQuestionBySubject}
            maximumMark={parseFloat(question?.mark ?? 0).toFixed(2)}
            negativeMark={parseFloat(question?.negative_mark ?? 0).toFixed(2)}
          />

          {/* Scrollable question content — grows to fill space, scrolls internally */}
          <Box
            sx={{
              padding: { xs: "12px", sm: "20px" },
              flex: 1,           // takes all remaining height
              overflowY: "auto", // THIS is the only thing that scrolls
              overflowX: "hidden",
              paddingBottom: "8px",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": { background: "#ccc", borderRadius: "4px" },
              "& img": { maxWidth: "100%", height: "auto" },
              "& table": { display: "block", overflowX: "auto", maxWidth: "100%" },
            }}
          >
            <Typography sx={{ fontSize: { xs: "0.92rem", sm: "1rem" }, lineHeight: 1.6 }}>
              {selectedLanguage === "english" ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: question?.question_en,
                  }}
                />
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: question?.question_hi,
                  }}
                />
              )}
            </Typography>

            {/* Options — extra bottom padding on mobile so last option isn't hidden behind fixed button bar */}
            <Box sx={{ pt: 1.5, pb: { xs: 10, sm: 6 } }}>
              {question?.options?.map((option, index) => {
                const isSelected = optionId === option.id;
                return (
                  <Box
                    key={option.id || index}
                    onClick={() => handleOptionSelect(option.id)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      padding: { xs: "8px 0", sm: "10px 0" },
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": { background: "#f5f7ff" },
                      px: 1,
                      transition: "background 0.15s",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: { xs: 28, sm: 32 },
                        height: { xs: 28, sm: 32 },
                        borderRadius: "50%",
                        border: isSelected ? "2px solid #4caf50" : "1.5px solid #aaa",
                        backgroundColor: isSelected ? "#4caf50" : "transparent",
                        color: isSelected ? "white" : "#333",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: { xs: "0.78rem", sm: "0.875rem" },
                        flexShrink: 0,
                        transition: "all 0.2s",
                      }}
                    >
                      {String.fromCharCode(65 + index)}
                    </Box>
                    <Box
                      sx={{ fontSize: { xs: "0.88rem", sm: "0.95rem" }, pt: "4px", "& img": { maxWidth: "100%", height: "auto" } }}
                    >
                      {selectedLanguage === "english" ? (
                        <span dangerouslySetInnerHTML={{ __html: option.option_en }} />
                      ) : (
                        <span dangerouslySetInnerHTML={{ __html: option.option_hi }} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      <ButtonSection
        currentQuestion={currentQuestion}
        handleSaveNextDefault={handleSaveNextDefault}
        handleMarkReviewDefault={handleMarkReviewDefault}
        handleClearResponse={handleClearResponse}
        setOptionId={setOptionId}
        handleBack={handleBack}
        isLast={questionData.length === currentQuestion + 1}
        handleOpenSubmitModal={handleOpenSubmitModal}
      />
    </Box>
  );
}

export default QuestionSection;
